/**
 * Date handling.
 * - Date-only values (due dates, meeting dates, milestones) are plain 'YYYY-MM-DD'
 *   strings end-to-end; they are never converted through a timezone.
 * - Event timestamps are stored in UTC and displayed in the user's timezone.
 * - "Today" and relative phrases are interpreted in the user's configured timezone.
 */
import { DEFAULT_TIMEZONE } from "./constants.js";

export const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isDateOnly(value: unknown): value is string {
  if (typeof value !== "string" || !DATE_ONLY_RE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function isValidTimeZone(tz: string) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Calendar date of `now` in the given timezone. */
export function todayInTz(tz: string = DEFAULT_TIMEZONE, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function addDays(dateOnly: string, n: number): string {
  const [y, m, d] = dateOnly.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

export function weekday(dateOnly: string): number {
  const [y, m, d] = dateOnly.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function compareDateOnly(a: string, b: string) {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Monday-based week containing the date. */
export function weekRange(dateOnly: string): { from: string; to: string } {
  const wd = weekday(dateOnly);
  const offset = wd === 0 ? -6 : 1 - wd;
  const from = addDays(dateOnly, offset);
  return { from, to: addDays(from, 6) };
}

/** Human display of a date-only value without timezone conversion. */
export function formatDateOnly(dateOnly: string | null | undefined, locale = "en-GB"): string {
  if (!dateOnly) return "Not recorded";
  if (!isDateOnly(dateOnly)) return dateOnly;
  const [y, m, d] = dateOnly.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

/** Display a UTC timestamp in the configured timezone. */
export function formatTimestamp(iso: string | Date | null | undefined, tz: string = DEFAULT_TIMEZONE, locale = "en-GB"): string {
  if (!iso) return "Not recorded";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "Invalid date";
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** Calendar date of a UTC timestamp in a timezone. */
export function dateOfTimestamp(iso: string | Date, tz: string = DEFAULT_TIMEZONE): string {
  return todayInTz(tz, typeof iso === "string" ? new Date(iso) : iso);
}

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export interface DateInterpretation {
  date: string;
  phrase: string;
  explanation: string;
}

/**
 * Suggest a calendar date for a relative phrase, relative to `today` (already in the
 * user's timezone). The result is a labelled suggestion — callers must not apply it
 * without user confirmation.
 */
export function interpretRelativeDate(phrase: string, today: string, tz: string = DEFAULT_TIMEZONE): DateInterpretation | null {
  const p = phrase.toLowerCase().replace(/^(by|on|before|until|due)\s+/, "").trim();
  const explain = (date: string, how: string) => ({ date, phrase, explanation: `Interpreted "${phrase}" as ${date} (${how}; today is ${today} in ${tz}).` });
  if (p === "today") return explain(today, "today");
  if (p === "tomorrow") return explain(addDays(today, 1), "tomorrow");
  const inN = /^in\s+(\d+)\s+(day|days|week|weeks)$/.exec(p);
  if (inN) {
    const n = Number(inN[1]) * (inN[2].startsWith("week") ? 7 : 1);
    return explain(addDays(today, n), `${n} days from today`);
  }
  if (p === "next week") {
    const { from } = weekRange(addDays(today, 7));
    return explain(from, "Monday of next week — ambiguous");
  }
  if (p === "end of week" || p === "end of the week") {
    return explain(addDays(weekRange(today).from, 4), "Friday of this week — ambiguous");
  }
  if (p === "end of month" || p === "end of the month" || p === "next month" || p === "this month") {
    const [y, m] = today.split("-").map(Number);
    if (p === "next month") {
      const dt = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
      return explain(dt, "first day of next month — ambiguous");
    }
    const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    return explain(last, "last day of this month — ambiguous");
  }
  const wd = /^(next|this)?\s*(sunday|monday|tuesday|wednesday|thursday|friday|saturday)$/.exec(p);
  if (wd) {
    const target = WEEKDAYS.indexOf(wd[2]);
    const cur = weekday(today);
    let delta = (target - cur + 7) % 7;
    if (delta === 0) delta = 7;
    if (wd[1] === "next" && delta < 7) {
      // "next Friday" is ambiguous; we choose the occurrence in the following week.
      const { from } = weekRange(addDays(today, 7));
      const d = addDays(from, (target + 6) % 7);
      return explain(d, `${wd[2]} of next week — ambiguous`);
    }
    return explain(addDays(today, delta), `next occurrence of ${wd[2]}`);
  }
  return null;
}

/** Offset (minutes) of a timezone from UTC at a given instant. */
export function tzOffsetMinutes(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);
  const v = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(v("year"), v("month") - 1, v("day"), v("hour"), v("minute"), v("second"));
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/** UTC instant at which the given calendar day starts in the timezone. */
export function zonedDayStartUtc(dateOnly: string, tz: string = DEFAULT_TIMEZONE): Date {
  const [y, m, d] = dateOnly.split("-").map(Number);
  const guess = Date.UTC(y, m - 1, d, 0, 0, 0);
  const off = tzOffsetMinutes(tz, new Date(guess));
  let t = guess - off * 60_000;
  const off2 = tzOffsetMinutes(tz, new Date(t));
  if (off2 !== off) t = guess - off2 * 60_000;
  return new Date(t);
}
